/**
 * Default form shape for the transfer wizard (Angular `propertyEditForm` in transfer-process.component.ts).
 */
import type { UseFormReset, UseFormSetValue, FieldValues, Path } from "react-hook-form"
import type { TransferWizardFormValues } from "../types"
import { validateTransferData } from "./transferValidateData"

export function defaultTransferFormValues(): TransferWizardFormValues {
  const emptyPay = {
    payment_method: "Checkings",
    routing_number: "",
    account_number: "",
    property_method: "",
    payee_name: "",
    other_payee_name: "",
    payment_address: "",
    payment_shop: "",
    payment_state: "",
    payment_city: "",
    payment_zipcode: "",
    verify: "",
    pay_to: "",
    property_address: "",
    property_address2: "",
    property_city: "",
    property_state: "",
    property_zipcode: "",
    units_count: "",
  }
  return {
    old_company_id: "",
    fk_company_id: "",
    property_id: "",
    property_name: "",
    new_property_name: "",
    listed: "",
    payment_method: "Checkings",
    routing_number: "",
    account_number: "",
    property_method: "",
    payee_name: "",
    other_payee_name: "",
    payment_address: "",
    payment_shop: "",
    payment_state: "",
    payment_city: "",
    payment_zipcode: "",
    verify: "",
    pay_to: "",
    company_name: "",
    newdomain: "",
    updatedomain: "0",
    property_address: "",
    property_address2: "",
    property_city: "",
    property_state: "",
    property_zipcode: "",
    units_count: "",
    newpropertyinfo: { ...emptyPay },
  }
}

function str(v: unknown): string {
  if (v == null) return ""
  return String(v)
}

/**
 * When GET omits paper payment street fields, copy from `company_*` or `property_*` (same idea as Angular
 * `transfer-process` confirm: `payment_address` ← `company_address` when payment fields are blank).
 */
function applyPaymentStreetFallbackToPatch(
  out: Partial<TransferWizardFormValues>,
  v: Record<string, unknown>,
): void {
  const pa = String(out.payment_address ?? "").trim()
  const pc = String(out.payment_city ?? "").trim()
  if (pa || pc) return

  const ca = v.company_address != null ? String(v.company_address).trim() : ""
  const cc = v.company_city != null ? String(v.company_city).trim() : ""
  const cs = v.company_state != null ? String(v.company_state).trim() : ""
  const cz = v.company_zipcode != null ? String(v.company_zipcode).trim() : ""
  const c2 = v.company_address2 != null ? String(v.company_address2).trim() : ""

  if (ca || cc || cs || cz || c2) {
    if (ca) out.payment_address = ca
    if (cc) out.payment_city = cc
    if (cs) out.payment_state = cs
    if (cz) out.payment_zipcode = cz
    if (c2 && !String(out.payment_shop ?? "").trim()) out.payment_shop = c2
    return
  }

  const pra = v.property_address != null ? String(v.property_address).trim() : ""
  const prc = v.property_city != null ? String(v.property_city).trim() : ""
  const prs = v.property_state != null ? String(v.property_state).trim() : ""
  const prz = v.property_zipcode != null ? String(v.property_zipcode).trim() : ""
  const pr2 = v.property_address2 != null ? String(v.property_address2).trim() : ""

  if (pra || prc || prs || prz || pr2) {
    if (pra) out.payment_address = pra
    if (prc) out.payment_city = prc
    if (prs) out.payment_state = prs
    if (prz) out.payment_zipcode = prz
    if (pr2 && !String(out.payment_shop ?? "").trim()) out.payment_shop = pr2
  }
}

/** Flatten API property/company object into transfer form fields (Angular `patchValue(validateData(response))`). */
export function buildTransferFormPatchFromEntity(
  response: Record<string, unknown>,
): Partial<TransferWizardFormValues> {
  const v = validateTransferData({ ...response }) as Record<string, unknown>
  const keys: (keyof Omit<TransferWizardFormValues, "newpropertyinfo">)[] = [
    "fk_company_id",
    "property_name",
    "listed",
    "payment_method",
    "routing_number",
    "account_number",
    "property_method",
    "payee_name",
    "other_payee_name",
    "payment_address",
    "payment_shop",
    "payment_state",
    "payment_city",
    "payment_zipcode",
    "verify",
    "pay_to",
    "company_name",
    "property_address",
    "property_address2",
    "property_city",
    "property_state",
    "property_zipcode",
    "units_count",
  ]
  const out: Partial<TransferWizardFormValues> = {}
  for (const k of keys) {
    if (v[k] !== undefined) (out as Record<string, string>)[k] = str(v[k])
  }
  const noRegex = /^-?\d+$/
  const pn = v.payee_name
  if (pn && pn !== "LEASING" && typeof pn === "string" && !noRegex.test(pn)) {
    out.other_payee_name = pn
    out.payee_name = "OTHER"
  }
  const npKeys: (keyof TransferWizardFormValues["newpropertyinfo"])[] = [
    "payment_method",
    "routing_number",
    "account_number",
    "property_method",
    "payee_name",
    "other_payee_name",
    "payment_address",
    "payment_shop",
    "payment_state",
    "payment_city",
    "payment_zipcode",
    "verify",
    "pay_to",
    "property_address",
    "property_address2",
    "property_city",
    "property_state",
    "property_zipcode",
    "units_count",
  ]
  const np: TransferWizardFormValues["newpropertyinfo"] = { ...defaultTransferFormValues().newpropertyinfo }
  for (const k of npKeys) {
    if (v[k] !== undefined) np[k] = str(v[k])
  }
  if (v.payee_name && v.payee_name !== "LEASING" && typeof v.payee_name === "string" && !noRegex.test(String(v.payee_name))) {
    np.other_payee_name = String(v.payee_name)
    np.payee_name = "OTHER"
  }
  applyPaymentStreetFallbackToPatch(out, v)
  out.newpropertyinfo = np
  return out
}

export function resetTransferFormFromProperty(
  reset: UseFormReset<TransferWizardFormValues>,
  response: Record<string, unknown>,
  extras?: Partial<TransferWizardFormValues>,
): void {
  reset({
    ...defaultTransferFormValues(),
    ...buildTransferFormPatchFromEntity(response),
    ...extras,
  })
}

export function patchPayeeFromPropertyData(
  setValue: UseFormSetValue<TransferWizardFormValues>,
  propertyData: Record<string, unknown>,
): void {
  const noRegex = /^-?\d+$/
  const pn = propertyData.payee_name
  if (pn && pn !== "LEASING" && typeof pn === "string" && !noRegex.test(pn)) {
    setValue("other_payee_name", String(pn))
    setValue("payee_name", "OTHER")
  }
}

export function setValueUnknown<T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  name: Path<T>,
  value: unknown,
): void {
  setValue(name, str(value) as never)
}

export type { TransferWizardFormValues } from '../types/utils'

